import React from 'react';
import { ExternalLink, Github, ArrowUpRight } from 'lucide-react';
import { projects } from '../data/mock';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const Projects = () => {
  const { t, language } = useLanguage();

  return (
    <section id="projects" className="py-32 px-6 relative">
      {/* Background accent */}
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-fuchsia-600/10 rounded-full blur-[150px]" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-20">
          <span className="text-purple-400 font-mono text-sm tracking-wider uppercase">{t('projects.label')}</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 text-white">
            {t('projects.title')} <span className="gradient-text">{t('projects.titleHighlight')}</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 mx-auto rounded-full" />
        </div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <article 
              key={project.id}
              className="group relative rounded-2xl bg-[hsl(270,40%,8%)] border border-purple-500/10 overflow-hidden card-hover"
            >
              {/* Project image placeholder */}
              <div className="aspect-video bg-gradient-to-br from-purple-900/50 to-fuchsia-900/30 flex items-center justify-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                  <Github className="w-8 h-8 text-purple-400" />
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-purple-300 transition-colors duration-300">
                  {project.title[language] || project.title.en}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {project.description[language] || project.description.en}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.map((tag, i) => (
                    <Badge 
                      key={i}
                      variant="secondary"
                      className="bg-purple-500/10 text-purple-300 border-purple-500/30 hover:bg-purple-500/20"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                {/* Links */}
                <div className="flex items-center gap-3">
                  <a 
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-400 transition-colors duration-300"
                    aria-label={`${project.title[language]} - ${t('projects.code')}`}
                  >
                    <Github className="w-4 h-4" />
                    <span>{t('projects.code')}</span>
                  </a>
                  <a 
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-400 transition-colors duration-300"
                    aria-label={`${project.title[language]} - ${t('projects.demo')}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{t('projects.demo')}</span>
                  </a>
                </div>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-purple-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </article>
          ))}
        </div>

        {/* View more button */}
        <div className="text-center mt-12">
          <Button 
            variant="outline"
            className="px-8 py-6 text-lg border-purple-500/50 text-purple-300 hover:bg-purple-500/10 hover:text-white rounded-xl transition-all duration-300 group"
            onClick={() => window.open('https://github.com/karaniJS', '_blank')}
          >
            {t('projects.moreBtn')}
            <ArrowUpRight className="w-5 h-5 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
